import { Icon, Label, Popup, Step, Table } from 'semantic-ui-react';
import FinancialNotesModal from '../../FinancialNotesModal';
import displayWorkedDays from '../displayWorkedDays';
import displayScheduledDays from '../displayScheduledDays';
import { compareByDate } from '../../../utils/commonutils';
import {
    formatCurrency,
    percentageBillableAllowedContracts,
    sortPracticesByWorkedDays,
    practiceHasData,
} from '../services';
import PracticesWithTooltip from './PracticesWithTooltip';
import ContractTypeLabel from './ContractTypeLabel';
import AdjustmentWriteOffCell from './AdjustmentWriteOffCell';
import BCareCell from './BCareCell';
import TotalPayCell from './TotalPayCell';
import moment from 'moment';

/**
 * Level-0 provider row rendered inside the dentist pay report table.
 *
 * All computed values and callbacks are passed in from the parent so this
 * component remains a pure presentational component.
 */
const ProviderRow = ({
    hideColumn,
    row,
    rowId,
    isProviderLocked,
    levelOneIcon,
    selectedDoctors,
    expandableColumn,
    expandableRowLevel2,
    isRampApplicableForPractice,
    isProviderOwnerOfPractice,
    isCustomRates,
    isHynContract,
    getPracticeName,
    contract,
    hansFinancialNote,
    providerQualifiesForTier,
    isProdMove,
    getContractDetails,
    filters,
    showAsterisk,
    PerOfbillableCareHYG,
    calendarRedirectUrl,
    bcareFn,
    pracHynBillableCareTotal,
    futureRates,
    currentRates,
    onSaveLock,
    onExpandRow,
    onPreviewDetails,
    onSelectDoctor,
    onExclude,
    onGetWriteOff,
    onGetAdjustment,
    onGetProduction,
}) => {
    const isAccountFilteredMode = (filters.account?.length > 0) || (filters.excludeAccount?.length > 0);
    const isRegularDoc = row.contractType !== 'UKG HYN' && row.contractType !== 'TMPDOC' && row.contractType !== 'TMPHYN';
    const sortedPracticesArray = sortPracticesByWorkedDays(Object.keys(row.data), row.data)
        .filter(practice => practiceHasData(row.data[practice]));
    const futurePayRates = (futureRates ?? [])
        .filter(f => f.practiceId === 0)
        .sort((a, b) => moment(a.range[0]).diff(moment(b.range[0])))
        .map(f => ({ range: f.range, rate: f.value?.dailyRatePay }));

    const isRecreasing = futurePayRates?.length > 1 ? futurePayRates[1].rate < futurePayRates[0].rate : undefined;
    const nextPayColor = isRecreasing === undefined ? undefined : isRecreasing ? 'red' : 'green';
    return (
        <tr>
            {!hideColumn.selectAll && (
                <td className="checkbox-column">
                    <input
                        type="checkbox"
                        name={row.ABBR}
                        checked={row.ABBR && isRegularDoc
                            ? selectedDoctors.indexOf((row.ABBR).toLocaleLowerCase()) >= 0
                            : false
                        }
                        onChange={isRegularDoc ? (e) => onSelectDoctor(e) : () => void (0)}
                    />
                </td>
            )}
            {!hideColumn.lockStatus && (
                <td className="lock-status-column">
                    <Icon
                        onClick={onSaveLock}
                        color={isProviderLocked >= 0 ? 'red' : 'green'}
                        size={"large"}
                        name={isProviderLocked >= 0 ? 'lock' : 'lock open'}
                    />
                </td>
            )}
            {!hideColumn.provider && (
                <td className={`${row.doctorNameFromProvider ? "red" : ""} `}>
                    <Icon
                        onClick={onExpandRow}
                        className={`${levelOneIcon} red`}
                        size='small'
                    />
                    <span onClick={onPreviewDetails}>
                        {row.doctorName || row.doctorNameFromProvider}
                    </span>
                    {onExclude && (
                        <Icon
                            name="eye slash outline"
                            size="small"
                            className="provider-exclude-btn"
                            title="Exclude this provider from rows, totals and export"
                            onClick={onExclude}
                        />
                    )}
                </td>
            )}
            {!hideColumn.ABBR && expandableColumn && <td></td>}
            {!hideColumn.practice && (
                <td>
                    <PracticesWithTooltip
                        practicesArray={sortedPracticesArray}
                        isRampApplicableForPractice={isRampApplicableForPractice}
                        isProviderOwnerOfPractice={isProviderOwnerOfPractice}
                        rowId={rowId}
                        isCustomRates={isCustomRates}
                        isHynContract={isHynContract}
                        getPracticeName={getPracticeName}
                    />
                </td>
            )}
            {!hideColumn.contractType && (
                <ContractTypeLabel
                    row={row}
                    contract={contract}
                    hansFinancialNote={hansFinancialNote}
                    providerQualifiesForTier={providerQualifiesForTier}
                    isProdMove={isProdMove}
                    getContractDetails={getContractDetails}
                    tedFilter={filters.tedFilter}
                />
            )}
            {!hideColumn.workTimeType && expandableColumn && <td></td>}
            {!hideColumn.workedDays && (
                <td>{displayWorkedDays(row.numberOfWorkedDays, row.sameDateMultiplePractices)}</td>
            )}
            {!hideColumn.scheduledDays && (
                <td>{displayScheduledDays(row.totalScheduledDays ?? row.numberOfScheduledDays, row.datesScheduledInMultiplePractices)}</td>
            )}
            {!hideColumn.RTOApproved && expandableColumn && <td></td>}
            {!hideColumn.FTOApproved && expandableColumn && <td></td>}
            {!hideColumn.patientVisits && <td>{row.patientVisits}</td>}
            {!hideColumn.bCPV && <td>{formatCurrency(row.bCPV)}</td>}
            {!hideColumn.patientVisitsPerDay && <td>{row.patientVisitsPerDay}</td>}
            {!hideColumn.rampStartDate && expandableColumn && <td></td>}
            {!hideColumn.rampEndDate && expandableColumn && <td />}
            {!hideColumn.procedureDate && expandableRowLevel2 && <td></td>}
            {!hideColumn.startEnd && expandableRowLevel2 && <td></td>}
            {!hideColumn.care && <td>{formatCurrency(row.price)}</td>}
            {!hideColumn.writeOffs && (
                <AdjustmentWriteOffCell
                    type="writeoff"
                    writeOffType={row.writeOffType}
                    amount={row.writeOffPrice}
                    onClickHandler={onGetWriteOff}
                />
            )}
            {!hideColumn.adjustments && (
                <AdjustmentWriteOffCell
                    type="adjustment"
                    writeOffType={row.writeOffType}
                    amount={row.adjustmentPrice}
                    onClickHandler={onGetAdjustment}
                />
            )}
            {!hideColumn.careFraction && <td>{row.prodAdjWo}%</td>}
            {!hideColumn.collectionsGoxGozTotal && expandableColumn && (
                <td>
                    {(row.contractType === 'Collections' || row.contractType === 'Collections w/HYN' || row.contractType === 'Collections Offset')
                        ? formatCurrency(parseFloat(row.GoxNonBillableCare))
                        : (row.contractType === 'GOZ' || row.contractType === 'GOX')
                            ? formatCurrency(parseFloat(row.GoxNonBillableCare ? row.GoxNonBillableCare : 0))
                            : (() => { const t = Object.values(row.data || {}).reduce((s, p) => s + (p.rawTedCollections || 0), 0); return t > 0 ? formatCurrency(t) : ''; })()}
                </td>
            )}
            {!hideColumn.bCare && (
                <BCareCell
                    bCare={row.bCare}
                    providerTotalHygienistBillableCare={row.hynBillableCare}
                    abbr={row.ABBR}
                    onClickHandler={onGetProduction}
                    showAsterisk={showAsterisk}
                />
            )}
            {!hideColumn.totalBasePay && (
                <td>
                    <div className='flex'>
                        {row.displayBasePay && row.contractType !== 'Prod Hurdle' ? formatCurrency(row.displayBasePay) : '-'}
                        {row.roleBCareFiltered && row.payEstimated &&
                            <Popup
                                trigger={<Icon name='calculator' color='blue' className='ml-1' />}
                                content='Estimated pay — re-imagined from Dentist/Oral Surgeon days only, not an official figure.'
                            />
                        }
                        {row.roleBCareFiltered && !row.payEstimated &&
                            <Popup
                                trigger={<Icon name='info circle' color='grey' className='ml-1' />}
                                content={row.isProviderOnTierBonus
                                    ? 'Reflects the full period — Tier Bonus pay can\'t be re-estimated from a day subset yet.'
                                    : 'Reflects the full period — not narrowed by the Dentist/Oral Surgeon day filter.'}
                            />
                        }
                        {
                            isRecreasing !== undefined && !!row.displayBasePay && row.contractType !== 'Prod Hurdle' &&
                            <Popup on='click' trigger={<Icon name='level down alternate' className='ml-1 cursorPointer' color={nextPayColor} />}>
                                <Popup.Content>
                                    <h4 className='mb-0 p-2'>Future day rate changes</h4>
                                    <Table striped celled className='mt-0' compact size='small'>
                                        <Table.Body>
                                            {
                                                futurePayRates.map((f, i) => <Table.Row key={'future-rate-' + i}>
                                                    <Table.Cell content={`${f.range[0]} - ${f.range[1] ?? 'FUTURE'}`} />
                                                    <Table.Cell content={formatCurrency(f.rate)} />
                                                </Table.Row>)
                                            }
                                        </Table.Body>
                                    </Table>
                                </Popup.Content>
                            </Popup>
                        }
                    </div>
                </td>
            )}
            {!hideColumn.financialNotes && (
                <td>
                    <FinancialNotesModal
                        financialNoteTotal={formatCurrency(row.financialNotes)}
                        financialNotes={row?.financialNoteList?.sort(compareByDate)}
                        trigger={
                            <Label
                                basic={true}
                                title={`Financial Note: ${formatCurrency(row.financialNotes)}\n BCare: ${formatCurrency(bcareFn)}`}
                                className="cursorPointer primaryColor d-flex"
                            >
                                {!!bcareFn && <Icon name="heartbeat" color="blue" />}
                                {row.financialNotes ? formatCurrency(row.financialNotes) : '-'}
                            </Label>
                        }
                    />
                </td>
            )}
            {!hideColumn.totalPay && (
                <TotalPayCell
                    row={row}
                    calendarRedirectUrl={calendarRedirectUrl}
                    pracHynBillableCareTotal={pracHynBillableCareTotal}
                    showAsterisk={showAsterisk}
                    useAccountFilteredPay={isAccountFilteredMode}
                />
            )}
            {!hideColumn.normalizedPercentageOfBillableCare && (
                percentageBillableAllowedContracts.has(row.contractType) ? (
                    <td>
                        <b>
                            {(
                                isAccountFilteredMode
                                    ? row.percBillableByAccount
                                    : row.normalizedPercentageOfBillableCare ?? 0
                            ).toFixed(2)}%
                        </b>
                        {showAsterisk && PerOfbillableCareHYG > 0
                            ? <p>*{PerOfbillableCareHYG.toFixed(2)}%*</p>
                            : ''}
                    </td>
                ) : <td></td>
            )}
        </tr>
    );
};

export default ProviderRow;
